'use client'

import { Button } from '@vinted/web-ui'
import { useEffect, useRef, useState } from 'react'

import { useAuthModal } from '@marketplace-web/registration/identity-feature'
import {
  useBrowserNavigation,
  navigateToPage,
} from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  buyerBuyEvent,
  buyerWantItemEvent,
} from '@marketplace-web/item-seller-action-buttons/item-action-buttons-data'
import { getHomepageSessionIdFromUrl } from '@marketplace-web/home/home-page-util'
import { getSearchSessionData } from '@marketplace-web/search/search-feature'
import {
  ConversationInitiatorType,
  CONVERSATION_URL,
  ApplicableTransactionAction,
} from '@marketplace-web/messaging/conversation-messaging-data'
import {
  GenericErrorModal,
  useNavigateToCheckout,
} from '@marketplace-web/checkout/checkout-feature'
import {
  CheckoutOrderTypeMap,
  ExtraServiceOrderType,
} from '@marketplace-web/checkout/checkout-data'

import { useCreateConversation } from '@marketplace-web/messaging/conversation-messaging-api-resolvers-feature'

import { ItemPageBuyButtonModel } from './types'
import { useShowStickyButtons, useCanTransact } from '../hooks'
import { incrementItemPageBuyButtonShowCounter } from '../../utils/observability'

type Props = {
  data: ItemPageBuyButtonModel
  isStickyButton?: boolean
}

const ItemPageBuyButtonPlugin = ({ data, isStickyButton = false }: Props) => {
  const {
    itemId,
    sellerId,
    canBuy,
    canInstantBuy,
    isReserved,
    isHidden,
    reservedForUserId,
    closetCountdownEndDate,
  } = data

  const translate = useTranslate()
  const { track } = useTracking()
  const { searchParams } = useBrowserNavigation()
  const canTransact = useCanTransact({
    canBuy,
    canInstantBuy,
    isReserved,
    isHidden,
    reservedForUserId,
    closetCountdownEndDate,
  })
  const { openAuthModal } = useAuthModal()

  const { user } = useSession()

  const { conversation, error, createConversation } = useCreateConversation()

  const { navigateToCheckout } = useNavigateToCheckout()

  const isAuthenticated = !!user
  const currentUserId = user?.id
  const isViewingOwnItem = sellerId === currentUserId?.toString()

  const showStickyButtons = useShowStickyButtons({ isViewingOwnItem })

  const isObserved = useRef(false)
  const [uiState, setUiState] = useState(UiState.Idle)
  const [isErrorModalShown, setIsErrorModalShown] = useState(false)

  const referrerScreen = searchParams.referrer?.toString()
  const searchSessionData = getSearchSessionData()
  const searchSessionIdRef = useRef(
    referrerScreen === 'catalog' ? searchSessionData.searchSessionId : null,
  )

  const isButtonHidden = isViewingOwnItem || !canTransact || showStickyButtons !== isStickyButton

  useEffect(() => {
    if (!error) return

    setIsErrorModalShown(true)
    setUiState(UiState.Idle)
  }, [error])

  useEffect(() => {
    if (isObserved.current) return

    isObserved.current = true
    incrementItemPageBuyButtonShowCounter(!isButtonHidden)
  }, [isButtonHidden])

  useEffect(() => {
    if (isButtonHidden) return
    if (!conversation?.transaction) return
    if (uiState !== UiState.Pending) return

    const { transaction } = conversation

    const handleCheckoutNavigation = async () => {
      const isError = await navigateToCheckout(
        transaction.id,
        CheckoutOrderTypeMap[ExtraServiceOrderType.Transaction],
      )

      if (isError) {
        setIsErrorModalShown(true)
        setUiState(UiState.Idle)
      }
    }

    track(
      buyerWantItemEvent({
        transactionId: transaction.id,
        itemId: Number(itemId),
        button: 'buy',
        globalSearchSessionId: searchSessionData.globalSearchSessionId,
        globalCatalogBrowseSessionId: searchSessionData.globalCatalogBrowseSessionId,
        searchSessionId: searchSessionIdRef.current,
        homepageSessionId: getHomepageSessionIdFromUrl(),
      }),
    )

    if (transaction.availableActions.includes(ApplicableTransactionAction.Buy)) {
      track(
        buyerBuyEvent({
          transactionId: transaction.id,
          screen: 'item',
          globalSearchSessionId: searchSessionData.globalSearchSessionId,
          globalCatalogBrowseSessionId: searchSessionData.globalCatalogBrowseSessionId,
          homepageSessionId: getHomepageSessionIdFromUrl(),
        }),
      )

      handleCheckoutNavigation()
    } else {
      navigateToPage(CONVERSATION_URL(conversation.id))
    }
  }, [
    itemId,
    conversation,
    searchSessionData.globalSearchSessionId,
    searchSessionData.globalCatalogBrowseSessionId,
    track,
    uiState,
    isButtonHidden,
    navigateToCheckout,
  ])

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (uiState === UiState.Pending) {
      event.preventDefault()

      return
    }

    if (!isAuthenticated) {
      openAuthModal()

      return
    }

    setUiState(UiState.Pending)

    createConversation({
      itemId,
      receiverId: sellerId,
      initiator: ConversationInitiatorType.Buy,
    })
  }

  if (isButtonHidden) return null

  return (
    <>
      <Button
        styling={Button.Styling.Filled}
        text={translate('item.actions.buy')}
        onClick={handleClick}
        testId="item-buy-button"
        disabled={uiState === UiState.Pending}
        isLoading={uiState === UiState.Pending}
      />
      <GenericErrorModal isShown={isErrorModalShown} onClose={() => setIsErrorModalShown(false)} />
    </>
  )
}

export default ItemPageBuyButtonPlugin
